import { PlusOutlined } from '@ant-design/icons'
import { useSuspenseQuery } from '@apollo/client'
import { FetchViewsDocument, ViewConfigTypeEnum, type ViewFieldsFragment } from '@salescore/client-api'
import { getOrganizationIdFromPath, SuspenseWithLoading } from '@salescore/client-common'
import { App, Button, Divider, Form, Select } from 'antd'
import { Array, pipe } from 'effect'
import { t } from 'i18next'
import { type ReactElement, useEffect, useMemo } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'

import { kpiFormModifiedAtom, viewsContextAtom } from '../../../../recoil/view/atoms'
import { buildKpiPivotFormModalProperties } from '../../../../rsheet/components/modal/KpiPivotFormModal'
import { ViewListItem } from './ListItem/ViewListItem'

export const KpiFormDashboardIdsFormItem = ({
  view,
  dashboardIds,
  setDashboardIds,
}: {
  view: ViewFieldsFragment
  dashboardIds: string[]
  setDashboardIds: (dashboardIds: string[]) => void
}): ReactElement => (
  <Form.Item label={t(`表示するダッシュボード`)}>
    <SuspenseWithLoading type="select">
      <Picker view={view} dashboardIds={dashboardIds} setDashboardIds={setDashboardIds} />
    </SuspenseWithLoading>
  </Form.Item>
)

const Picker = ({
  view,
  dashboardIds,
  setDashboardIds,
}: {
  view: ViewFieldsFragment
  dashboardIds: string[]
  setDashboardIds: (dashboardIds: string[]) => void
}): ReactElement => {
  const { onAddResource } = useRecoilValue(viewsContextAtom)
  const { modal } = App.useApp()
  const {
    data: { views },
    refetch: invalidate,
  } = useSuspenseQuery(FetchViewsDocument, {
    variables: {
      organizationId: getOrganizationIdFromPath(),
      type: ViewConfigTypeEnum.KpiPivot,
    },
  })

  const setFormModified = useSetRecoilState(kpiFormModifiedAtom)

  const filteredViews = useMemo(() => {
    // ゴミ箱のビューは除外。
    const unarchivedViews = views.filter((kpiPivotView) => !kpiPivotView.archived)
    // 公開設定は「公開ダッシュボードから非公開KPIを参照」だけがダメ。すなわち、KPIが公開されているか、ダッシュボードが非公開ならOK。
    return view.private ? unarchivedViews.filter((kpiPivotView) => kpiPivotView.private) : unarchivedViews
  }, [view.private, views])

  useEffect(() => {
    if (dashboardIds.length === 0) {
      // まだセットされていないとき
      setDashboardIds(
        // 登録済みの値をformにセットする
        // ※ 本来はfetchViewsの結果から導出したdashboardIdsをformの初期値にすべき。
        // ※ 当初、formのdashboardIdsはinitializeViewMutation.ts#generateInitialKpiForm()で初期化していた。
        //   fetchViewsの結果から初期値を導出する処理をそこに入れた方が綺麗かも。
        pipe(
          filteredViews,
          Array.filter((x) => x.config.type === 'kpiPivot' && x.config.kpis.some((kpi) => kpi.viewId === view.id)),
          Array.map((x) => x.id),
        ),
      )
    }
  }, [filteredViews])

  return (
    <Select
      showSearch
      filterOption={(input, option) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,@typescript-eslint/strict-boolean-expressions,@typescript-eslint/no-unnecessary-condition
        ((option?.key as string) || '').toLowerCase().includes(input.toLowerCase()) ?? false
      }
      optionLabelProp="label"
      mode="multiple"
      value={dashboardIds}
      onChange={(value) => {
        setDashboardIds(value)
        setFormModified(true)
      }}
      popupRender={(menu) => (
        <>
          {menu}
          <Divider style={{ margin: '8px 0' }} />
          <Button
            type="text"
            className="w-full text-blue-500"
            icon={<PlusOutlined />}
            onClick={() => {
              const m = modal.confirm(
                buildKpiPivotFormModalProperties({
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
                  onCreate: async (viewId) => {
                    setDashboardIds([...dashboardIds, viewId])
                    setFormModified(true)
                    await onAddResource({ viewIds: [viewId] })
                    void invalidate()
                    m.destroy()
                  },
                }),
              )
            }}
          >
            {t(`新規作成`)}
          </Button>
        </>
      )}
    >
      {filteredViews.map((view) => (
        <Select.Option value={view.id} label={view.name} key={`${view.id}-${view.name}-${view.createdBy?.name ?? ''}`}>
          <ViewListItem view={view} />
        </Select.Option>
      ))}
    </Select>
  )
}
